import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  AddMemberForNonSmkFamilyThunkArg,
  AddNonSMKFormValues,
  AddNonSMKThunkArg,
  EditMemberForNonSmkFamilyThunkArg,
  EditMemberThunkArg,
  Family,
  FamilyMaster,
  FamilyMember,
  FamilyrequestData,
  MasterdataBasic,
  Members,
  NewHeadForOldFamily,
  RemoveMembersThunkArg,
  SplitMembersThunkArg,
  TransferMembersThunkArg,
} from "~/interface/family/family";
import API_PATH from "~/utils/api-routes";
import { createSmkApi, setLatestOtpToken } from "~/utils/smkInterseptor";
import { RootState } from "../store";
import { AxiosError } from "axios";
import { getCookie } from "~/utils/cookies";
import { AUTH_COOKIE_TOKEN } from "~/utils/constant";

// Step type

type familyOptionSteps =
  | "view"
  | "split"
  | "transfer"
  | "remove"
  | "edit"
  | "add"
  | "list"
  | "success"
  | "head"
  | "EditFamily"
  | "nonSmk"
  | "editNonSmk";

// State type
interface initialStateData {
  isMyFamilyDialogOpen: boolean;
  step: familyOptionSteps | null;
  loading: boolean;
  error: string | null;
  selectedFamilyMember: FamilyMember | null;
  selectedFamilyMembers: FamilyMember[];
  goToStep: familyOptionSteps | null;
  family: Family | null;
  familyMembers: FamilyMember[];
  authToken: string | null;
  fillRemoveReason: boolean; // Optional field for RemoveMember component
  reasonForRemoveMembers: {
    reason: string;
    note: string | null;
    aksharvas_date?: Date | string | null; // Optional field for aksharvas date
  };
  familyMaster: FamilyMaster | null;
  newHeadForOldFamilyData: NewHeadForOldFamily | null; // Optional field for NewHeadForOldFamily component
  hasFamilyHead: boolean; // Optional field to check if family has a head
  smkStatus?: "hof" | string; // Optional field for SMK status
  familyRequestData: FamilyrequestData[];
}

const initialState: initialStateData = {
  isMyFamilyDialogOpen: false,
  step: "view",
  loading: false,
  error: null,
  selectedFamilyMember: null,
  selectedFamilyMembers: [],
  goToStep: null,
  family: null,
  familyMembers: [],
  authToken: null,
  fillRemoveReason: false, // Initialize to false
  reasonForRemoveMembers: {
    reason: "",
    note: null,
  },
  familyMaster: null,
  newHeadForOldFamilyData: null, // Initialize to null
  hasFamilyHead: false, // Initialize to false
  smkStatus: undefined, // Initialize to undefined
  familyRequestData: [],
};

const getFamilyId = (getState: () => unknown): number => {
  const state = getState() as RootState;
  const id = state.myFamily?.family?.id;
  if (!id) throw new Error("Family ID not found");
  return id;
};

const token = getCookie(AUTH_COOKIE_TOKEN);
setLatestOtpToken(token ?? "");
const smkApi = createSmkApi();

// Create the splitMembersThunk using createAsyncThunk
export const splitMembersThunk = createAsyncThunk(
  "myFamily/splitMembers",
  async ({ token, data }: SplitMembersThunkArg, { getState, rejectWithValue }) => {
    try {
      const familyId = getFamilyId(getState);
      const response = await smkApi.post(`${API_PATH.SPLIT_MEMBERS}/${familyId}`, data);
      return response.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "An error occurred while splitting family members";
      return rejectWithValue(message);
    }
  }
);

// Create the transferMembersThunk using createAsyncThunk
export const transferMembersThunk = createAsyncThunk(
  "myFamily/transferMembers",
  async ({ token, data }: TransferMembersThunkArg, { getState, rejectWithValue }) => {
    try {
      const familyId = getFamilyId(getState);
      const response = await smkApi.post(`${API_PATH.TRANSFER_MEMBERS}/${familyId}`, data);
      return response.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "An error occurred while transferring family members";
      return rejectWithValue(message);
    }
  }
);

// Create the removeMembersThunk using createAsyncThunk
export const removeMembersThunk = createAsyncThunk(
  "myFamily/removeMembers",
  async ({ token, data }: RemoveMembersThunkArg, { getState, rejectWithValue }) => {
    try {
      const familyId = getFamilyId(getState);
      const response = await smkApi.post(`${API_PATH.REMOVE_MEMBERS}/${familyId}`, data);
      return response.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "An error occurred while removing family members";
      return rejectWithValue(message);
    }
  }
);

// Create the addNonSMKThunk using createAsyncThunk
export const addNonSMKThunk = createAsyncThunk("myFamily/addNonSMK", async ({ token, data }: AddNonSMKThunkArg, { getState, rejectWithValue }) => {
  try {
    const familyId = getFamilyId(getState);
    const response = await smkApi.post(`${API_PATH.ADD_NON_SMK}/${familyId}`, data);
    return response.data;
  } catch (error) {
    const err = error as AxiosError<{ message: string }>;
    const message = err.response?.data?.message || err.message || "An error occurred while adding non-SMK family members";
    return rejectWithValue(message);
  }
});

//create the editMemberThunk using createAsyncThunk
export const editMemberThunk = createAsyncThunk("myFamily/editMember", async ({ token, data }: EditMemberThunkArg, { getState, rejectWithValue }) => {
  try {
    const familyId = getFamilyId(getState);
    const response = await smkApi.post(`${API_PATH.EDIT_MEMBER}/${familyId}`, data);
    return response.data;
  } catch (error) {
    const err = error as AxiosError<{ message: string }>;
    const message = err.response?.data?.message || err.message || "An error occurred while editing family members";
    return rejectWithValue(message);
  }
});

//create the edit family thunk using createAsyncThunk
export const editFamilyThunk = createAsyncThunk(
  "myFamily/editFamily",
  async (
    {
      token,
      data,
    }: {
      token: string;
      data: {
        new_address?: string;
        new_zone?: string;
        new_sub_zone?: string;
        is_hof_change: boolean;
        new_hof_smk?: number;
        old_family_rwhof?: Members[];
      };
    },
    { getState, rejectWithValue }
  ) => {
    try {
      const familyId = getFamilyId(getState);
      const response = await smkApi.post(`${API_PATH.EDIT_FAMILY_DETAILS}/${familyId}`, data);
      return response.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "An error occurred while editing family details";
      return rejectWithValue(message);
    }
  }
);

//create the cancel request thunk using createAsyncThunk
export const cancelRequestThunk = createAsyncThunk(
  "myFamily/cancelRequest",
  async ({ token, requestId }: { token: string; requestId: number }, { getState, rejectWithValue }) => {
    try {
      const response = await smkApi.put(`${API_PATH.CANCEL_REQUEST}/${requestId}`);
      return response.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "An error occurred while canceling request";
      return rejectWithValue(message);
    }
  }
);

// add member which has from has no SMK or non SMK family
export const addMemberForNonSmkFamilyThunk = createAsyncThunk(
  "myFamily/addMemberForNonSmkFamily",
  async ({ token, data }: AddMemberForNonSmkFamilyThunkArg, { getState, rejectWithValue }) => {
    try {
      const response = await smkApi.post(`${API_PATH.FAMILY_MEMBER}`, data);
      return response.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "An error occurred while adding member for non-SMK family";
      return rejectWithValue(message);
    }
  }
);

// edit member which has from has no SMK or non SMK family
export const editMemberForNonSmkFamilyThunk = createAsyncThunk(
  "myFamily/editMemberForNonSmkFamily",
  async ({ token, data }: EditMemberForNonSmkFamilyThunkArg, { getState, rejectWithValue }) => {
    try {
      const { id, ...restData } = data; // Destructure to get id and rest of the data
      const response = await smkApi.put(`${API_PATH.FAMILY_MEMBER}/${id}`, restData);
      return response.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "An error occurred while editing member for non-SMK family";
      return rejectWithValue(message);
    }
  }
);

// delete member which has from has no SMK or non SMK family
export const deleteMemberForNonSmkFamilyThunk = createAsyncThunk(
  "myFamily/deleteMemberForNonSmkFamily",
  async ({ token, memberId }: { token: string; memberId: number }, { getState, rejectWithValue }) => {
    try {
      const response = await smkApi.delete(`${API_PATH.FAMILY_MEMBER}/delete/${memberId}`);
      return memberId;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "An error occurred while deleting member for non-SMK family";
      return rejectWithValue(message);
    }
  }
);

//edit non SMK family details
export const editNonSmkFamilyThunk = createAsyncThunk(
  "myFamily/editNonSmkFamily",
  async ({ token, data }: { token: string; data: AddNonSMKFormValues }, { getState, rejectWithValue }) => {
    try {
      const familyId = getFamilyId(getState);
      const response = await smkApi.put(`${API_PATH.EDIT_NON_SMK_FAMILY}/${familyId}`, data);
      return response.data;
    } catch (error) {
      const err = error as AxiosError<{ message: string }>;
      const message = err.response?.data?.message || err.message || "An error occurred while editing non-SMK family";
      return rejectWithValue(message);
    }
  }
);

const myFamilySlice = createSlice({
  name: "myFamily",
  initialState,
  reducers: {
    setFamilyOptionsStep: (state, action: PayloadAction<familyOptionSteps | null>) => {
      state.step = action.payload;
      state.isMyFamilyDialogOpen = action.payload !== "add" && action.payload !== "nonSmk" && true;
    },
    setIsFamilyDialogOpen: (state) => {
      state.isMyFamilyDialogOpen = true;
    },
    setIsFamilyDialogClose: (state) => {
      state.isMyFamilyDialogOpen = false;
      state.error = null;
      state.goToStep = null;
      state.loading = false;
      state.selectedFamilyMember = null;
      state.selectedFamilyMembers = [];
      state.step = null;
      state.fillRemoveReason = false; // Reset fillRemoveReason when dialog is closed
      state.hasFamilyHead = false; // Reset hasFamilyHead when dialog is closed
      state.newHeadForOldFamilyData = null; // Reset newHeadForOldFamilyData
    },
    setErrorNull: (state) => {
      state.error = null;
    },
    setSelectedFamilyMember: (state, action: PayloadAction<FamilyMember | null>) => {
      state.selectedFamilyMember = action.payload;
    },
    setSelectedFamilyMembers: (state, action: PayloadAction<FamilyMember[]>) => {
      state.selectedFamilyMembers = action.payload;
    },
    setGoToStep: (state, action: PayloadAction<familyOptionSteps | null>) => {
      state.goToStep = action.payload;
    },
    removeSelectedMember: (state, action) => {
      const idToRemove = action.payload;
      state.selectedFamilyMembers = state.selectedFamilyMembers.filter((member) => member.id !== idToRemove);
    },
    setFamily: (state, action: PayloadAction<Family | null>) => {
      state.family = action.payload;
    },
    setFamilyMembers: (state, action: PayloadAction<FamilyMember[]>) => {
      state.familyMembers = action.payload;
    },
    setFamilyAuthToken: (state, action: PayloadAction<string | null>) => {
      state.authToken = action.payload;
    },
    setFillRemoveReason: (state, action: PayloadAction<boolean>) => {
      state.fillRemoveReason = action.payload;
    },
    setReasonForRemoveMembers: (state, action: PayloadAction<{ reason: string; note: string | null }>) => {
      state.reasonForRemoveMembers = action.payload;
    },
    setFamilyMaster: (state, action: PayloadAction<FamilyMaster>) => {
      state.familyMaster = action.payload;
    },
    setNewHeadForOldFamilyData: (state, action: PayloadAction<NewHeadForOldFamily | null>) => {
      state.newHeadForOldFamilyData = action.payload;
    },
    setHasFamilyHead: (state, action: PayloadAction<boolean>) => {
      state.hasFamilyHead = action.payload;
    },
    setSmkStatus: (state, action: PayloadAction<"hof" | string | undefined>) => {
      state.smkStatus = action.payload;
    },
    setFamilyRequestData: (state, action: PayloadAction<FamilyrequestData[]>) => {
      state.familyRequestData = action.payload;
    },
  },
  extraReducers: (builder) => {
    //splitMembersThunk
    builder
      .addCase(splitMembersThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(splitMembersThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedFamilyMembers = [];
        state.hasFamilyHead = false; // Reset hasFamilyHead after successful split
        state.newHeadForOldFamilyData = null; // Reset newHeadForOldFamilyData
        state.step = "success";
      })
      .addCase(splitMembersThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    //transferMembersThunk
    builder
      .addCase(transferMembersThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(transferMembersThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedFamilyMembers = [];
        state.hasFamilyHead = false; // Reset hasFamilyHead after successful transfer
        state.newHeadForOldFamilyData = null; // Reset newHeadForOldFamilyData
        state.step = "success";
      })
      .addCase(transferMembersThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // removeMembersThunk
    builder
      .addCase(removeMembersThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(removeMembersThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedFamilyMembers = [];
        state.hasFamilyHead = false; // Reset hasFamilyHead after successful removal
        state.newHeadForOldFamilyData = null; // Reset newHeadForOldFamilyData
        state.step = "success";
        state.goToStep = null;
        state.fillRemoveReason = false; // Reset fillRemoveReason after successful removal
      })
      .addCase(removeMembersThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // addNonSMKThunk
    builder
      .addCase(addNonSMKThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addNonSMKThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.isMyFamilyDialogOpen = true;
        state.goToStep = null;
        state.step = "success";
      })
      .addCase(addNonSMKThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // editMemberThunk
    builder
      .addCase(editMemberThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editMemberThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.isMyFamilyDialogOpen = true;
        state.goToStep = null;
        state.step = "success";
      })
      .addCase(editMemberThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // editFamilyThunk
    builder
      .addCase(editFamilyThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editFamilyThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.isMyFamilyDialogOpen = true;
        state.goToStep = null;
        state.step = "success";
      })
      .addCase(editFamilyThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // cancelRequestThunk
    builder
      .addCase(cancelRequestThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(cancelRequestThunk.fulfilled, (state, action) => {
        state.loading = false;
        // Optionally, you can remove the request from familyRequestData if needed
        state.familyRequestData = state.familyRequestData.filter((request) => request.id !== action.meta.arg.requestId);
      })
      .addCase(cancelRequestThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // addMemberForNonSmkFamilyThunk
    builder
      .addCase(addMemberForNonSmkFamilyThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addMemberForNonSmkFamilyThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.familyMembers.push(action.payload.data);
        state.smkStatus = state.familyMembers.some((member) => member.relation_with_hof === "self") ? "hof" : "member"; // Update SMK status based on family members
      })
      .addCase(addMemberForNonSmkFamilyThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // editMemberForNonSmkFamilyThunk
    builder
      .addCase(editMemberForNonSmkFamilyThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editMemberForNonSmkFamilyThunk.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.familyMembers.findIndex((member) => member.id === action.payload.data.id);
        if (index !== -1) {
          state.familyMembers[index] = action.payload.data;
        }
        state.step = null;
        state.goToStep = null;
        state.selectedFamilyMember = null; // Reset selectedFamilyMember after editing
        state.isMyFamilyDialogOpen = false; // Close dialog after editing
      })
      .addCase(editMemberForNonSmkFamilyThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // deleteMemberForNonSmkFamilyThunk
    builder
      .addCase(deleteMemberForNonSmkFamilyThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteMemberForNonSmkFamilyThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.familyMembers = state.familyMembers.filter((member) => member.id !== action.payload);
        state.step = null;
        state.goToStep = null;
        state.fillRemoveReason = false; // Reset fillRemoveReason after deleting
        state.selectedFamilyMember = null; // Reset selectedFamilyMember after deleting
        state.isMyFamilyDialogOpen = false; // Close dialog after deleting
      })
      .addCase(deleteMemberForNonSmkFamilyThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });

    // editNonSmkFamilyThunk
    builder
      .addCase(editNonSmkFamilyThunk.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editNonSmkFamilyThunk.fulfilled, (state, action) => {
        state.loading = false;
        state.step = null;
        state.goToStep = null;
        state.selectedFamilyMember = null; // Reset selectedFamilyMember after editing
        state.isMyFamilyDialogOpen = false; // Close dialog after editing
        state.family = action.payload.data;
      })
      .addCase(editNonSmkFamilyThunk.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  setFamilyOptionsStep,
  setIsFamilyDialogOpen,
  setIsFamilyDialogClose,
  setErrorNull,
  setSelectedFamilyMember,
  setSelectedFamilyMembers,
  setGoToStep,
  removeSelectedMember,
  setFamily,
  setFamilyMembers,
  setFamilyAuthToken,
  setFillRemoveReason,
  setReasonForRemoveMembers,
  setFamilyMaster,
  setNewHeadForOldFamilyData,
  setHasFamilyHead,
  setSmkStatus,
  setFamilyRequestData,
} = myFamilySlice.actions;

export default myFamilySlice.reducer;
